import React from "react";
import { useMemo, useState } from "react";
import { useQueries } from "@tanstack/react-query";
import {
  Box,
  Typography,
  Chip,
  Pagination,
  Stack,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
} from "@mui/material";
import { graphQLClient } from "@/graphqlClient";
import { GET_CMS_HAT_CONTENT_MS } from "@/api/queries";
import { useMSHatIndex } from "@/hooks/useCMSData";
import { useGetDistrictById } from "@/hooks/useGetDistrictById";
import { useCurrentUser } from "@/hooks/useCurrentUser";

// Utility to extract district id from userPath (same pattern used elsewhere)
const useCurrentDistrict = () => {
  const currentUser = useCurrentUser();

  const districtId = React.useMemo(() => {
    if (!currentUser?.userPath) return null;
    const match = currentUser.userPath.match(/dis-[^#]+/);
    return match ? match[0] : null;
  }, [currentUser?.userPath]);
  const { data: district } = useGetDistrictById(districtId);
  return district;
};

export const HatsDebug = () => {
  const { hatIndex, loading: hatsLoading, error: hatsError } = useMSHatIndex();
  const district = useCurrentDistrict();

  const [page, setPage] = useState(1);
  const pageSize = 10;
  const [regionType, setRegionType] = useState("country");
  const [skillsSort, setSkillsSort] = useState({}); // { [hatId]: { column, direction } }

  const sortedHatIndex = useMemo(() => {
    return (hatIndex || []).slice().sort((a, b) =>
      (a?.displayName || "").localeCompare(b?.displayName || "", undefined, {
        sensitivity: "base",
      }),
    );
  }, [hatIndex]);

  const totalPages = useMemo(() => {
    return sortedHatIndex.length > 0
      ? Math.ceil(sortedHatIndex.length / pageSize)
      : 0;
  }, [sortedHatIndex]);

  const hatsForPage = useMemo(() => {
    const start = (page - 1) * pageSize;
    const end = start + pageSize;
    return sortedHatIndex.slice(start, end);
  }, [sortedHatIndex, page]);

  const stateId = district?.state ?? 36; // default from existing usage
  const msaId = district?.msa ?? -1; // default from existing usage
  const districtId = district?.id;

  const queries = useQueries({
    queries: hatsForPage.map((hat) => ({
      queryKey: ["debug-cmsHatContentMS", hat.id, stateId, msaId, districtId],
      queryFn: async () => {
        return graphQLClient.request(GET_CMS_HAT_CONTENT_MS, {
          hatId: hat.id,
          stateId,
          msaId,
          districtId,
        });
      },
      enabled: !!hat?.id,
      staleTime: 0,
      gcTime: 1000 * 60 * 15,
    })),
  });

  const isLoadingAny = hatsLoading || queries.some((q) => q.isLoading);
  const isErrorAny = hatsError || queries.some((q) => q.isError);

  const items = useMemo(() => {
    // Build items for the selected region type
    return hatsForPage.map((hat, idx) => {
      const data = queries[idx]?.data;
      const regions = data?.cmsHatsRegions || [];
      const selectedEntry = regions.find((r) => r?.region?.type === regionType);

      // Top 5 skills like KnowledgeSkillsPrepPay
      const rawSkills = selectedEntry?.skills || [];
      const nonSpecialized = rawSkills.filter(
        (s) => s?.skill?.skillType !== "Specialized",
      );
      const sorted = [...nonSpecialized].sort(
        (a, b) => (b?.percentage || 0) - (a?.percentage || 0),
      );
      const deduped = sorted.filter((item, index, array) => {
        const currentLabel = item?.skill?.powerskill
          ? item?.skill?.powerskillLabel
          : item?.skill?.name;
        const firstIndex = array.findIndex((prevItem) => {
          const prevLabel = prevItem?.skill?.powerskill
            ? prevItem?.skill?.powerskillLabel
            : prevItem?.skill?.name;
          return currentLabel === prevLabel;
        });
        return firstIndex === index;
      });
      const topFiveSkills = deduped.slice(0, 5);

      return {
        hatId: hat.id,
        displayName: selectedEntry?.hat?.displayName || null,
        hasRegionData: !!selectedEntry,
        regionName: selectedEntry?.region?.name || null,
        regionType,
        topFiveSkills,
        allSkills: rawSkills || [],
      };
    });
  }, [hatsForPage, queries, regionType]);

  const filteredItems = useMemo(
    () => items.filter((i) => i.hasRegionData),
    [items],
  );

  const getHatSort = (hatId) =>
    skillsSort[hatId] || { column: "percent", direction: "desc" };

  const handleRequestSort = (hatId, column) => {
    setSkillsSort((prev) => {
      const current = prev[hatId] || { column: "percent", direction: "desc" };
      const direction =
        current.column === column && current.direction === "asc"
          ? "desc"
          : "asc";
      return { ...prev, [hatId]: { column, direction } };
    });
  };

  const getSortedSkills = (item, skills) => {
    const sort = getHatSort(item.hatId);
    const toValue = (s) => {
      switch (sort.column) {
        case "name":
          return (s?.skill?.name || "").toLowerCase();
        case "type":
          return (s?.skill?.skillType || "").toLowerCase();
        case "powerskill":
          return s?.skill?.powerskill ? 1 : 0;
        case "label":
          return (
            s?.skill?.powerskill ? s?.skill?.powerskillLabel || "" : ""
          ).toLowerCase();
        case "percent":
        default:
          return Number(s?.percentage || 0);
      }
    };
    const comparator = (a, b) => {
      const aVal = toValue(a);
      const bVal = toValue(b);
      if (aVal === bVal) return 0;
      const base = aVal < bVal ? -1 : 1;
      return sort.direction === "asc" ? base : -base;
    };
    return (skills || []).slice().sort(comparator);
  };

  return (
    <Box p={3}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography variant="h4">Hats Debug</Typography>
        <Chip
          label={`Total Hats: ${hatIndex?.length || 0}`}
          color="primary"
          variant="outlined"
        />
      </Stack>

      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} mb={2}>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel id="region-type-label">Region Type</InputLabel>
          <Select
            labelId="region-type-label"
            label="Region Type"
            value={regionType}
            onChange={(e) => setRegionType(e.target.value)}
          >
            <MenuItem value="country">Country</MenuItem>
            <MenuItem value="state">State</MenuItem>
            <MenuItem value="msa">MSA</MenuItem>
          </Select>
        </FormControl>
      </Stack>

      {isLoadingAny && (
        <Typography sx={{ mb: 2 }}>Loading hats/content…</Typography>
      )}
      {isErrorAny && (
        <Typography color="error" sx={{ mb: 2 }}>
          Error loading data
        </Typography>
      )}

      <Grid container spacing={2} sx={{ mb: 2 }}>
        {filteredItems.map((item) => (
          <Grid key={item.hatId} item xs={12} sm={12} md={6} lg={6}>
            <Card
              variant="outlined"
              sx={{
                height: "100%",
                borderColor: "divider",
                "&:hover": { boxShadow: 4 },
              }}
            >
              <CardContent>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="start"
                  mb={1}
                >
                  <Typography
                    variant="subtitle1"
                    color={item.displayName ? "text.primary" : "text.disabled"}
                  >
                    {item.displayName || "No data"}
                  </Typography>
                </Stack>
                {item.regionName && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1 }}
                  >
                    Region: {item.regionName}
                  </Typography>
                )}
                <Typography variant="caption" color="text.secondary">
                  Hat ID: {item.hatId}
                </Typography>

                {item.topFiveSkills && item.topFiveSkills.length > 0 && (
                  <>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Top 5 Skills
                    </Typography>
                    <Stack
                      direction="row"
                      spacing={1}
                      useFlexGap
                      flexWrap="wrap"
                      mb={1}
                    >
                      {item.topFiveSkills.map((s) => {
                        const label = s?.skill?.powerskill
                          ? s?.skill?.powerskillLabel
                          : s?.skill?.name;
                        return (
                          <Chip
                            key={`${item.hatId}-${label}`}
                            label={`${label} (${Math.round(s?.percentage || 0)}%)`}
                            variant="outlined"
                            size="small"
                          />
                        );
                      })}
                    </Stack>
                  </>
                )}

                {item.allSkills && item.allSkills.length > 0 && (
                  <>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Common Skills (
                      {
                        item.allSkills.filter(
                          (s) => s?.skill?.skillType !== "Specialized",
                        ).length
                      }
                      )
                    </Typography>
                    <TableContainer
                      sx={{
                        maxHeight: 260,
                        border: 1,
                        borderColor: "divider",
                        borderRadius: 1,
                        mb: 2,
                      }}
                    >
                      <Table
                        size="small"
                        stickyHeader
                        aria-label="common skills table"
                      >
                        <TableHead>
                          <TableRow>
                            <TableCell
                              sortDirection={
                                getHatSort(item.hatId).column === "name"
                                  ? getHatSort(item.hatId).direction
                                  : false
                              }
                            >
                              <TableSortLabel
                                active={
                                  getHatSort(item.hatId).column === "name"
                                }
                                direction={
                                  getHatSort(item.hatId).column === "name"
                                    ? getHatSort(item.hatId).direction
                                    : "asc"
                                }
                                onClick={() =>
                                  handleRequestSort(item.hatId, "name")
                                }
                              >
                                Skill Name
                              </TableSortLabel>
                            </TableCell>
                            <TableCell
                              sortDirection={
                                getHatSort(item.hatId).column === "type"
                                  ? getHatSort(item.hatId).direction
                                  : false
                              }
                            >
                              <TableSortLabel
                                active={
                                  getHatSort(item.hatId).column === "type"
                                }
                                direction={
                                  getHatSort(item.hatId).column === "type"
                                    ? getHatSort(item.hatId).direction
                                    : "asc"
                                }
                                onClick={() =>
                                  handleRequestSort(item.hatId, "type")
                                }
                              >
                                Skill Type
                              </TableSortLabel>
                            </TableCell>
                            <TableCell
                              sortDirection={
                                getHatSort(item.hatId).column === "label"
                                  ? getHatSort(item.hatId).direction
                                  : false
                              }
                            >
                              <TableSortLabel
                                active={
                                  getHatSort(item.hatId).column === "label"
                                }
                                direction={
                                  getHatSort(item.hatId).column === "label"
                                    ? getHatSort(item.hatId).direction
                                    : "asc"
                                }
                                onClick={() =>
                                  handleRequestSort(item.hatId, "label")
                                }
                              >
                                Powerskill Label
                              </TableSortLabel>
                            </TableCell>
                            <TableCell
                              align="right"
                              sortDirection={
                                getHatSort(item.hatId).column === "percent"
                                  ? getHatSort(item.hatId).direction
                                  : false
                              }
                            >
                              <TableSortLabel
                                active={
                                  getHatSort(item.hatId).column === "percent"
                                }
                                direction={
                                  getHatSort(item.hatId).column === "percent"
                                    ? getHatSort(item.hatId).direction
                                    : "desc"
                                }
                                onClick={() =>
                                  handleRequestSort(item.hatId, "percent")
                                }
                              >
                                %
                              </TableSortLabel>
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {getSortedSkills(
                            item,
                            item.allSkills.filter(
                              (s) => s?.skill?.skillType !== "Specialized",
                            ),
                          ).map((s, idx) => (
                            <TableRow key={`${item.hatId}-common-${idx}`} hover>
                              <TableCell>{s?.skill?.name}</TableCell>
                              <TableCell>{s?.skill?.skillType}</TableCell>
                              <TableCell>
                                {s?.skill?.powerskill
                                  ? s?.skill?.powerskillLabel
                                  : "—"}
                              </TableCell>
                              <TableCell align="right">
                                {Math.round(s?.percentage || 0)}%
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>

                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Specialized Skills (
                      {
                        item.allSkills.filter(
                          (s) => s?.skill?.skillType === "Specialized",
                        ).length
                      }
                      )
                    </Typography>
                    <TableContainer
                      sx={{
                        maxHeight: 260,
                        border: 1,
                        borderColor: "divider",
                        borderRadius: 1,
                      }}
                    >
                      <Table
                        size="small"
                        stickyHeader
                        aria-label="specialized skills table"
                      >
                        <TableHead>
                          <TableRow>
                            <TableCell
                              sortDirection={
                                getHatSort(item.hatId).column === "name"
                                  ? getHatSort(item.hatId).direction
                                  : false
                              }
                            >
                              <TableSortLabel
                                active={
                                  getHatSort(item.hatId).column === "name"
                                }
                                direction={
                                  getHatSort(item.hatId).column === "name"
                                    ? getHatSort(item.hatId).direction
                                    : "asc"
                                }
                                onClick={() =>
                                  handleRequestSort(item.hatId, "name")
                                }
                              >
                                Skill Name
                              </TableSortLabel>
                            </TableCell>
                            <TableCell
                              sortDirection={
                                getHatSort(item.hatId).column === "type"
                                  ? getHatSort(item.hatId).direction
                                  : false
                              }
                            >
                              <TableSortLabel
                                active={
                                  getHatSort(item.hatId).column === "type"
                                }
                                direction={
                                  getHatSort(item.hatId).column === "type"
                                    ? getHatSort(item.hatId).direction
                                    : "asc"
                                }
                                onClick={() =>
                                  handleRequestSort(item.hatId, "type")
                                }
                              >
                                Skill Type
                              </TableSortLabel>
                            </TableCell>
                            <TableCell
                              sortDirection={
                                getHatSort(item.hatId).column === "label"
                                  ? getHatSort(item.hatId).direction
                                  : false
                              }
                            >
                              <TableSortLabel
                                active={
                                  getHatSort(item.hatId).column === "label"
                                }
                                direction={
                                  getHatSort(item.hatId).column === "label"
                                    ? getHatSort(item.hatId).direction
                                    : "asc"
                                }
                                onClick={() =>
                                  handleRequestSort(item.hatId, "label")
                                }
                              >
                                Powerskill Label
                              </TableSortLabel>
                            </TableCell>
                            <TableCell
                              align="right"
                              sortDirection={
                                getHatSort(item.hatId).column === "percent"
                                  ? getHatSort(item.hatId).direction
                                  : false
                              }
                            >
                              <TableSortLabel
                                active={
                                  getHatSort(item.hatId).column === "percent"
                                }
                                direction={
                                  getHatSort(item.hatId).column === "percent"
                                    ? getHatSort(item.hatId).direction
                                    : "desc"
                                }
                                onClick={() =>
                                  handleRequestSort(item.hatId, "percent")
                                }
                              >
                                %
                              </TableSortLabel>
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {getSortedSkills(
                            item,
                            item.allSkills.filter(
                              (s) => s?.skill?.skillType === "Specialized",
                            ),
                          ).map((s, idx) => (
                            <TableRow
                              key={`${item.hatId}-special-${idx}`}
                              hover
                            >
                              <TableCell>{s?.skill?.name}</TableCell>
                              <TableCell>{s?.skill?.skillType}</TableCell>
                              <TableCell>
                                {s?.skill?.powerskill
                                  ? s?.skill?.powerskillLabel
                                  : "—"}
                              </TableCell>
                              <TableCell align="right">
                                {Math.round(s?.percentage || 0)}%
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
        {filteredItems.length === 0 && (
          <Grid item xs={12}>
            <Card variant="outlined" sx={{ borderColor: "divider" }}>
              <CardContent>
                <Typography>No hats on this page</Typography>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      {totalPages > 1 && (
        <Box display="flex" justifyContent="center">
          <Pagination
            count={totalPages}
            page={page}
            onChange={(e, value) => setPage(value)}
            color="primary"
            showFirstButton
            showLastButton
          />
        </Box>
      )}
    </Box>
  );
};

export default HatsDebug;
